import moment from 'moment-timezone';
import suncalc from 'suncalc';

export type SunInfo = {
  solarNoon: number | null;
  nadir: number | null;
  sunrise: number | null;
  sunset: number | null;
  sunriseEnd: number | null;
  sunsetStart: number | null;
  dawn: number | null;
  dusk: number | null;
  nauticalDawn: number | null;
  nauticalDusk: number | null;
  nightEnd: number | null;
  night: number | null;
  goldenHourEnd: number | null;
  goldenHour: number | null;
};

export type SunInfoParams = {
  timestamp?: number;
  latitude: number;
  longitude: number;
};

/**
 * What kind of day this is at this latitude. These are not interchangeable and a
 * single "no data" fallback cannot express any of them:
 *
 * - `normal`               every event happens.
 * - `noAstronomicalNight`  the sun rises and sets, but never reaches 18° below
 *                          the horizon, so `nightEnd`/`night` are null. Not a
 *                          polar case: Paris is in this regime 13–30 June.
 * - `polarNight`           the sun never rises. Twilight events may still exist.
 * - `midnightSun`          the sun never sets.
 */
export type SunRegime =
  | 'normal'
  | 'noAstronomicalNight'
  | 'polarNight'
  | 'midnightSun';

/** Degrees. Azimuth is a compass bearing: 0 = N, 90 = E, 180 = S, 270 = W. */
export type SunPosition = {azimuth: number; altitude: number};

export type AltitudeSample = {
  /** Minutes since the start of the local day. */
  minute: number;
  /** Degrees above the horizon; negative below. */
  altitude: number;
};

export type SunTransition = {
  kind: 'firstSunrise' | 'firstSunset';
  timestamp: number;
  days: number;
};

const RAD_TO_DEG = 180 / Math.PI;
const SECONDS_PER_DAY = 86400;

const degreesToRadians = (degrees: number) => (degrees * Math.PI) / 180;
const MINUTES_PER_DAY = 1440;

/**
 * How far ahead `findNextTransition` will look. A polar winter at 69°N runs 48
 * days and a polar summer 68, so 190 clears both with room; at the poles proper
 * no transition exists at all and the walk has to stop somewhere.
 */
const MAX_TRANSITION_DAYS = 190;

/**
 * Half-width of the search around a solstice for the clock extremes.
 *
 * 45 clamps below roughly 12° of latitude, where the daylight curve is flat and
 * the extreme sits at the window edge; 75 clears it everywhere it is reported.
 */
const WINDOW_DAYS = 75;

/**
 * Below this latitude the four clock extremes are not reported at all. Daylight
 * barely varies, so the extremum is set by the equation of time rather than by
 * the season, the "solstice" anchor degenerates onto an equinox, and the answer
 * is a minutes-wide difference no reader can act on.
 */
const FLAT_LATITUDE = 10;

const toDate = (timestamp?: number) =>
  timestamp ? new Date(timestamp * 1000) : new Date();

const toTimestamp = (value: Date | null) =>
  value instanceof Date && !Number.isNaN(value.getTime())
    ? Math.floor(value.getTime() / 1000)
    : null;

const getInfo = ({timestamp, latitude, longitude}: SunInfoParams): SunInfo => {
  const rawTimes = suncalc.getTimes(toDate(timestamp), latitude, longitude);
  const info = {} as SunInfo;
  Object.entries(rawTimes).forEach(([k, v]) => {
    info[k] = toTimestamp(v as Date);
  });
  return info;
};

/**
 * Sun position in degrees. suncalc reports azimuth in radians measured from
 * south, which is not what anyone means by a bearing — this converts to the
 * compass convention so it can be shown as "WNW · 286°".
 */
const getPosition = ({
  timestamp,
  latitude,
  longitude,
}: SunInfoParams): SunPosition => {
  const {azimuth, altitude} = suncalc.getPosition(
    toDate(timestamp),
    latitude,
    longitude,
  );
  return {
    azimuth: (azimuth * RAD_TO_DEG + 180 + 360) % 360,
    altitude: altitude * RAD_TO_DEG,
  };
};

/**
 * Classify the day.
 *
 * Deliberately keyed off solar noon rather than a sampled curve: `solarNoon` is
 * the one event suncalc always returns, even when every rise/set event is null,
 * and the sun's altitude there is by definition the highest it gets. That makes
 * the polar branch exact instead of dependent on sampling resolution.
 */
const getRegime = ({
  timestamp,
  latitude,
  longitude,
}: SunInfoParams): SunRegime => {
  const info = getInfo({timestamp, latitude, longitude});

  if (info.sunrise !== null && info.sunset !== null) {
    return info.nightEnd === null || info.night === null
      ? 'noAstronomicalNight'
      : 'normal';
  }

  // suncalc derives sunrise and sunset from the same acos, so they are always
  // both present or both absent — never one of each. Reaching here means neither
  // exists, and the altitude at solar noon (the day's maximum by definition)
  // decides which way.
  const {altitude} = getPosition({
    timestamp: info.solarNoon === null ? timestamp : info.solarNoon,
    latitude,
    longitude,
  });
  return altitude > 0 ? 'midnightSun' : 'polarNight';
};

/**
 * Which span "light" means for a day. They are different quantities and must
 * never be differenced against each other — see `getLightDelta`.
 */
type LightMeasure = 'day' | 'civil';

/**
 * The measure to use when comparing two days. Civil twilight wins whenever
 * either side is a polar night, because sunrise-to-sunset is zero there and the
 * comparison would otherwise be against a quantity that does not exist.
 */
const measureFor = (a: SunRegime, b: SunRegime): LightMeasure =>
  a === 'polarNight' || b === 'polarNight' ? 'civil' : 'day';

const lightSecondsFor = (
  measure: LightMeasure,
  {timestamp, latitude, longitude}: SunInfoParams,
): number => {
  // Measured from suncalc's raw Dates, not from `getInfo`, whose timestamps are
  // floored to whole seconds for display. Differencing two floored values loses
  // up to a second, and the daily change is itself only a few minutes — the
  // Paris figure for 28 Aug is -204.74 s, which double flooring turns into -204
  // and rounds to the wrong second.
  const raw = suncalc.getTimes(toDate(timestamp), latitude, longitude);
  const span = (from: Date, to: Date) => (to.getTime() - from.getTime()) / 1000;
  const usable = (value: Date) => !Number.isNaN(value.getTime());

  if (measure === 'civil') {
    return usable(raw.dawn) && usable(raw.dusk) ? span(raw.dawn, raw.dusk) : 0;
  }
  if (getRegime({timestamp, latitude, longitude}) === 'midnightSun') {
    return SECONDS_PER_DAY;
  }
  return usable(raw.sunrise) && usable(raw.sunset)
    ? span(raw.sunrise, raw.sunset)
    : 0;
};

export type LightSummary = {
  /** Seconds of light today, measured by `measure`. */
  seconds: number;
  /** Change since yesterday, measured the same way. Negative = shortening. */
  delta: number;
  measure: LightMeasure;
};

/**
 * Today's light and how it changed, guaranteed to be the same quantity.
 *
 * The two must be derived together, or they end up describing different spans on
 * the days a polar regime starts or ends and contradict each other in sign.
 *
 * Steps back from solar noon rather than from the caller's timestamp: solar noon
 * is always mid-day, so subtracting 24 h cannot land on the same calendar date
 * across a DST boundary the way stepping back from 00:30 could.
 */
const getLightSummary = ({
  timestamp,
  latitude,
  longitude,
}: SunInfoParams): LightSummary => {
  const {solarNoon} = getInfo({timestamp, latitude, longitude});
  const noon = solarNoon ?? timestamp ?? Math.floor(Date.now() / 1000);
  const yesterday = noon - SECONDS_PER_DAY;

  // Today's own measure, applied to BOTH days.
  //
  // The bug this replaces measured each day on its own terms, so on Tromso's
  // first sunrise after the polar night it subtracted yesterday's ~5 h of civil
  // twilight from today's ~1 h of daylight and reported -4h49 on the morning the
  // sun came back.
  //
  // Choosing the measure from the *pair* fixes the arithmetic but hides the
  // event: that same day would then read "5h55 usable light" and never mention
  // that the sun rose. Measuring both days as daylight gives 1h00 - 0h00 = +1h00,
  // which is both correct and the thing worth saying.
  const todayRegime = getRegime({timestamp: noon, latitude, longitude});
  const measure = measureFor(todayRegime, todayRegime);
  const seconds = lightSecondsFor(measure, {
    timestamp: noon,
    latitude,
    longitude,
  });

  return {
    measure,
    seconds,
    delta:
      seconds -
      lightSecondsFor(measure, {timestamp: yesterday, latitude, longitude}),
  };
};

/**
 * Seconds of light in the day on its own terms: sunrise to sunset normally,
 * civil dawn to civil dusk through a polar night (when "day length" would
 * otherwise be a flat zero all winter), and the whole day under a midnight sun.
 *
 * Prefer `getLightSummary` when showing this next to the daily change — see the
 * note there.
 */
const getLightSeconds = (params: SunInfoParams): number => {
  const regime = getRegime(params);
  return lightSecondsFor(measureFor(regime, regime), params);
};

/** Change in light since yesterday, in seconds. See `getLightSummary`. */
const getLightDelta = (params: SunInfoParams): number =>
  getLightSummary(params).delta;

/**
 * Altitude across one local day, for drawing the twilight band.
 *
 * The band is coloured from this rather than from the event times, which is what
 * lets it render unchanged through a polar night: altitude is defined on every
 * date at every latitude, while ten of the fourteen event times are null under a
 * midnight sun.
 *
 * `startTimestamp` must be local midnight — callers already have it from
 * `Dates.getScaleStart(now, timezone, Dates.DAY)`. Use a `stepMinutes` that
 * divides 1440, or the last sample lands short of the end of the day.
 */
const getAltitudeCurve = ({
  startTimestamp,
  latitude,
  longitude,
  stepMinutes = 10,
  timezone,
}: {
  startTimestamp: number;
  latitude: number;
  longitude: number;
  stepMinutes?: number;
  /** Without it, sampling is 60 s strides and a DST day skews by an hour. */
  timezone?: string;
}): AltitudeSample[] => {
  const base = timezone
    ? moment.unix(startTimestamp).tz(timezone).startOf('day')
    : null;

  // Wall-clock minutes, not elapsed seconds. On the 25-hour day that ends DST,
  // `startTimestamp + minute * 60` reaches 23:00 local when the axis says 24:00,
  // colouring the whole strip an hour out of place.
  const instantAt = (minute: number) => {
    if (base === null) {
      return startTimestamp + minute * 60;
    }
    if (minute >= MINUTES_PER_DAY) {
      return base.clone().add(1, 'day').unix();
    }
    return base
      .clone()
      .hour(Math.floor(minute / 60))
      .minute(minute % 60)
      .unix();
  };

  if (stepMinutes <= 0) {
    throw new RangeError('getAltitudeCurve: stepMinutes must be positive');
  }

  const samples: AltitudeSample[] = [];
  let previous = Number.NEGATIVE_INFINITY;
  for (let minute = 0; minute <= MINUTES_PER_DAY; minute += stepMinutes) {
    // Setting a wall-clock time inside the spring-forward gap (02:00-03:00 does
    // not exist) resolves backwards, so samples 120-165 would repeat 60-105.
    // Clamp forward: the axis stays monotonic in time and the skipped hour is
    // night in every zone that transitions at 02:00.
    const instant = Math.max(instantAt(minute), previous + stepMinutes * 60);
    previous = instant;
    samples.push({
      minute,
      altitude: getPosition({timestamp: instant, latitude, longitude}).altitude,
    });
  }
  return samples;
};

/**
 * Wall-clock minutes since local midnight, always within [0, 1440).
 *
 * `(now - startOfDay) / 60` is elapsed seconds, which is an hour adrift for the
 * whole of a DST day and can exceed 1440 — enough to push a "now" marker off the
 * end of a 24-hour axis.
 *
 * During the repeated hour on a fall-back day both passes map to the same
 * minute, so a marker sits an hour early for one of them. One hour a year, at
 * night, and the alternative is an axis that is no longer a wall clock.
 */
const getLocalMinuteOfDay = (timestamp: number, timezone: string): number => {
  const local = moment.unix(timestamp).tz(timezone);
  return local.hours() * 60 + local.minutes();
};

/**
 * When the current polar regime ends — the first sunset of a midnight-sun
 * summer, or the first sunrise after a polar night.
 *
 * Returns null outside a polar regime, and also when no transition is found
 * within `MAX_TRANSITION_DAYS`: at the poles proper there genuinely is not one.
 */
const findNextTransition = ({
  timestamp,
  latitude,
  longitude,
}: SunInfoParams): SunTransition | null => {
  const regime = getRegime({timestamp, latitude, longitude});
  if (regime !== 'polarNight' && regime !== 'midnightSun') {
    return null;
  }

  const wanted = regime === 'polarNight' ? 'sunrise' : 'sunset';
  const from = getInfo({timestamp, latitude, longitude}).solarNoon ?? timestamp;
  if (from === undefined) {
    return null;
  }

  for (let days = 1; days <= MAX_TRANSITION_DAYS; days += 1) {
    const at = from + days * SECONDS_PER_DAY;
    const event = getInfo({timestamp: at, latitude, longitude})[wanted];
    if (event !== null) {
      return {
        kind: regime === 'polarNight' ? 'firstSunrise' : 'firstSunset',
        timestamp: event,
        days,
      };
    }
  }
  return null;
};

export type YearDay = {
  /** Local date, YYYY-MM-DD. */
  date: string;
  /** Local midday, which is what suncalc must be asked about. */
  timestamp: number;
  /** Light in that day's own measure — see `getLightSeconds`. */
  seconds: number;
  regime: SunRegime;
};

export type YearExtreme = {date: string; timestamp: number; seconds: number};
export type YearClockExtreme = {date: string; minuteOfDay: number};
export type YearPeriod = {from: string; to: string; days: number};

export type YearMilestones = {
  longestDay: YearExtreme | null;
  shortestDay: YearExtreme | null;
  /**
   * Null at any location that has polar days. "Earliest sunset" is not a fact
   * about a place where the sun does not set for two months, and printing the
   * edge of the polar period under that label would be a different claim.
   */
  earliestSunset: YearClockExtreme | null;
  latestSunset: YearClockExtreme | null;
  earliestSunrise: YearClockExtreme | null;
  latestSunrise: YearClockExtreme | null;
  /** Present only where they occur; these replace the clock extremes. */
  midnightSun: YearPeriod | null;
  polarNight: YearPeriod | null;
};

/**
 * Daylight for every day of a calendar year.
 *
 * Always sunrise-to-sunset, unlike `getLightSeconds`, which substitutes civil
 * twilight through a polar night so a card is not a flat zero all winter. Across
 * a year that substitution would compare twilight spans against daylight spans
 * and make the shortest day at Tromso come out as the 23-minute day the sun
 * returns rather than the two months it never rose. On a year curve the flat
 * zero *is* the picture.
 *
 * Sampled at local midday: suncalc resolves the solar day containing the instant
 * it is given, and local midnight is the previous day in any zone east of
 * Greenwich.
 */
const getYearLightCurve = ({
  year,
  latitude,
  longitude,
  timezone,
}: {
  year: number;
  latitude: number;
  longitude: number;
  timezone: string;
}): YearDay[] => {
  const days: YearDay[] = [];
  const cursor = moment
    .tz([year, 0, 1], timezone)
    .startOf('day')
    .add(12, 'hours');
  while (cursor.year() === year) {
    const timestamp = cursor.unix();
    const params = {timestamp, latitude, longitude};
    days.push({
      date: cursor.format('YYYY-MM-DD'),
      timestamp,
      seconds: lightSecondsFor('day', params),
      regime: getRegime(params),
    });
    cursor.add(1, 'day');
  }
  return days;
};

/**
 * Minutes from the local midnight that *starts* the sample day — deliberately
 * allowed past 1440.
 *
 * Wrapping to 0–1439 makes a 00:05 sunset score 5, which can never win a
 * "latest" comparison against a 23:59 one. At Fairbanks that reported 16 July
 * 23:58 for a latest sunset that is really 22 June at 00:49, a month and 51
 * minutes out under a label that reads as a hard fact.
 */
const minutesFromDayStart = (value: Date, dayStartMs: number): number | null =>
  value instanceof Date && !Number.isNaN(value.getTime())
    ? (value.getTime() - dayStartMs) / 60000
    : null;

/**
 * Contiguous runs of a regime.
 *
 * A polar night is normally TWO runs in a calendar year — it starts in November
 * and ends in the following January, so 1 January and 31 December are both
 * inside one. Tromsø's 2026 runs are 1–14 Jan and 28 Nov–31 Dec; reporting the
 * longer of them says "34 days" for a night that is about 48, ending on a date
 * that is a calendar artefact. When both ends of the year are in the regime the
 * two are stitched into the single period they actually are.
 */
const runsOf = (days: YearDay[], regime: SunRegime): YearPeriod[] => {
  const runs: YearPeriod[] = [];
  days.forEach((day, index) => {
    if (day.regime !== regime) {
      return;
    }
    if (index > 0 && days[index - 1].regime === regime) {
      const last = runs[runs.length - 1];
      last.to = day.date;
      last.days += 1;
    } else {
      runs.push({from: day.date, to: day.date, days: 1});
    }
  });

  if (
    runs.length > 1 &&
    runs[0].from === days[0].date &&
    runs[runs.length - 1].to === days[days.length - 1].date
  ) {
    const head = runs.shift() as YearPeriod;
    const tail = runs[runs.length - 1];
    tail.to = head.to;
    tail.days += head.days;
  }
  return runs;
};

/**
 * The dates worth arguing about, chosen from the regimes the location actually
 * has.
 *
 * At 48°N those are the solstices plus the earliest sunset and latest sunrise —
 * which fall a fortnight either side of the December solstice rather than on it,
 * because the equation of time moves faster than the declination there. At 69°N
 * they are the four transition dates instead.
 */
export type Equinox = {kind: 'march' | 'september'; timestamp: number};

/**
 * Periodic terms for Meeus, *Astronomical Algorithms*, ch. 27. Triples of
 * (A, B, C) in degrees; the sum of A·cos(B + C·T) corrects the mean instant.
 */
const EQUINOX_TERMS: [number, number, number][] = [
  [485, 324.96, 1934.136],
  [203, 337.23, 32964.467],
  [199, 342.08, 20.186],
  [182, 27.85, 445267.112],
  [156, 73.14, 45036.886],
  [136, 171.52, 22518.443],
  [77, 222.54, 65928.934],
  [74, 296.72, 3034.906],
  [70, 243.58, 9037.513],
  [58, 119.81, 33718.147],
  [52, 297.17, 150.678],
  [50, 21.02, 2281.226],
  [45, 247.54, 29929.562],
  [44, 325.15, 31555.956],
  [29, 60.93, 4443.417],
  [18, 155.12, 67555.328],
  [17, 288.79, 4562.452],
  [16, 198.04, 62894.029],
  [14, 199.76, 31436.921],
  [12, 95.39, 14577.848],
  [12, 287.11, 31931.756],
  [12, 320.81, 34777.259],
  [9, 227.73, 1222.114],
  [8, 15.45, 16859.074],
];

/** Mean-instant polynomials, also Meeus ch. 27, valid for years 1000-3000. */
const EQUINOX_MEAN = {
  march: [2451623.80984, 365242.37404, 0.05169, -0.00411, -0.00057],
  september: [2451810.21715, 365242.01767, -0.11575, 0.00337, 0.00078],
} as const;

const JULIAN_EPOCH = 2440587.5;
const J2000 = 2451545;
const JULIAN_CENTURY = 36525;

/**
 * The two equinox instants of a calendar year.
 *
 * Meeus ch. 27 rather than a search for the moment the declination crosses zero.
 * The obvious bisection is cheaper to write, but it can only be as accurate as
 * the solar longitude it is given: the low-precision series in ch. 25 is good to
 * 0.01°, which is a quarter of an hour of the sun's motion. That is fine for
 * "equinox in 24 days" and not fine for printing the date — the September
 * equinox can fall within minutes of midnight UTC, and a quarter hour of error
 * moves it to the previous day. This form is good to well under a minute.
 *
 * Returned in UTC without the ΔT correction (about 69 s in 2026), which is an
 * order of magnitude below anything this is displayed at.
 */
const getEquinoxes = (year: number): Equinox[] => {
  const y = (year - 2000) / 1000;
  return (['march', 'september'] as const).map((kind) => {
    const jde0 = EQUINOX_MEAN[kind].reduce(
      (total, coefficient, power) => total + coefficient * y ** power,
      0,
    );
    const t = (jde0 - J2000) / JULIAN_CENTURY;
    const w = degreesToRadians(35999.373 * t - 2.47);
    const lambda = 1 + 0.0334 * Math.cos(w) + 0.0007 * Math.cos(2 * w);
    const s = EQUINOX_TERMS.reduce(
      (total, [a, b, c]) => total + a * Math.cos(degreesToRadians(b + c * t)),
      0,
    );
    const jde = jde0 + (0.00001 * s) / lambda;
    return {
      kind,
      timestamp: Math.round((jde - JULIAN_EPOCH) * SECONDS_PER_DAY),
    };
  });
};

const getYearMilestones = ({
  year,
  latitude,
  longitude,
  timezone,
  curve,
}: {
  year: number;
  latitude: number;
  longitude: number;
  timezone: string;
  /** Pass a curve already computed for the same year to halve the work. */
  curve?: YearDay[];
}): YearMilestones => {
  const days =
    curve ?? getYearLightCurve({year, latitude, longitude, timezone});

  /**
   * The extreme day, or its midpoint when the extreme is a plateau.
   *
   * `>` alone keeps the *first* of 68 identical midnight-sun days, so Tromsø
   * read "Longest day · 19 May · 24h00" — the day the plateau started, not a
   * longest day. There is no single longest day there; the midpoint at least
   * points at the middle of the period the polar rows already name.
   *
   * The plateau is found as a *run*, with the year end stitched, because a polar
   * night straddles 31 December — see the comment on the runs below.
   */
  const byLight = (pick: (a: number, b: number) => boolean) => {
    const best = days.reduce<number | null>(
      (bestSeconds, day) =>
        bestSeconds === null || pick(day.seconds, bestSeconds)
          ? day.seconds
          : bestSeconds,
      null,
    );
    if (best === null) {
      return null;
    }
    const matching = days.reduce<number[]>((indexes, day, index) => {
      if (day.seconds === best) {
        indexes.push(index);
      }
      return indexes;
    }, []);

    /*
     * The midpoint of the *plateau*, not of the calendar-ordered match list.
     *
     * Tromso's 48-day zero plateau is cut by the calendar into 1-14 January and
     * 28 November-31 December. Taking the middle of the flat list counts 24 days
     * from 1 January and lands on 7 December, which is the middle of neither
     * piece and of nothing else either. The two ends are stitched here for the
     * same reason `runsOf` stitches them for the polar-night row.
     */
    const runs: number[][] = [];
    matching.forEach((index) => {
      const last = runs[runs.length - 1];
      if (last !== undefined && index === last[last.length - 1] + 1) {
        last.push(index);
      } else {
        runs.push([index]);
      }
    });
    if (runs.length > 1) {
      const first = runs[0];
      const last = runs[runs.length - 1];
      if (first[0] === 0 && last[last.length - 1] === days.length - 1) {
        runs.pop();
        runs.shift();
        runs.push([...last, ...first]);
      }
    }

    const plateau = runs.reduce((longest, run) =>
      run.length > longest.length ? run : longest,
    );
    const chosen = days[plateau[Math.floor((plateau.length - 1) / 2)]];
    return {
      date: chosen.date,
      timestamp: chosen.timestamp,
      seconds: chosen.seconds,
    };
  };

  const midnightSun = runsOf(days, 'midnightSun');
  const polarNight = runsOf(days, 'polarNight');
  const isPolar = midnightSun.length > 0 || polarNight.length > 0;

  /**
   * Searched in a window around its own solstice, not across the calendar year.
   *
   * The whole point of these four is that they do NOT fall on the solstice — the
   * equation of time moves faster than the declination there, so at 48°N the
   * earliest sunset is 7 December and the latest sunrise 29 December. Taking the
   * extreme over Jan-Dec instead returns 1 January, which belongs to the
   * *previous* winter and reads as nonsense next to "shortest day, 22 December".
   *
   * Minutes are measured from each sample day's own local midnight and are NOT
   * wrapped to 0-1439 — see `minutesFromDayStart`.
   */
  const byClock = (
    key: 'sunrise' | 'sunset',
    pick: (a: number, b: number) => boolean,
    around: YearExtreme | null,
  ): YearClockExtreme | null => {
    if (isPolar || around === null || Math.abs(latitude) < FLAT_LATITUDE) {
      return null;
    }
    let best: {date: string; minutes: number} | null = null;
    for (let offset = -WINDOW_DAYS; offset <= WINDOW_DAYS; offset += 1) {
      const sample = moment
        .unix(around.timestamp)
        .tz(timezone)
        .add(offset, 'days');
      const dayStart = sample.clone().startOf('day').valueOf();
      // Raw suncalc Dates, not `getInfo`, whose timestamps are floored to whole
      // seconds for display. 16 and 17 June differ by 0.011 min in Paris and
      // floor to the same second, so the flooring made the search pick the
      // first of the two rather than the real minimum.
      const raw = suncalc.getTimes(sample.toDate(), latitude, longitude)[key];
      const minutes = minutesFromDayStart(raw, dayStart);
      if (minutes === null) {
        continue;
      }
      if (best === null || pick(minutes, best.minutes)) {
        best = {date: sample.format('YYYY-MM-DD'), minutes};
      }
    }
    return best === null
      ? null
      : {
          date: best.date,
          // Back into a clock reading only for display.
          minuteOfDay: ((Math.floor(best.minutes) % 1440) + 1440) % 1440,
        };
  };

  const longest = (runs: YearPeriod[]) =>
    runs.reduce<YearPeriod | null>(
      (best, run) => (best === null || run.days > best.days ? run : best),
      null,
    );

  const longestDay = byLight((a, b) => a > b);
  const shortestDay = byLight((a, b) => a < b);

  return {
    longestDay,
    shortestDay,
    // Winter pair around the shortest day, summer pair around the longest.
    earliestSunset: byClock('sunset', (a, b) => a < b, shortestDay),
    latestSunrise: byClock('sunrise', (a, b) => a > b, shortestDay),
    latestSunset: byClock('sunset', (a, b) => a > b, longestDay),
    earliestSunrise: byClock('sunrise', (a, b) => a < b, longestDay),
    midnightSun: longest(midnightSun),
    polarNight: longest(polarNight),
  };
};

export {
  findNextTransition,
  getAltitudeCurve,
  getInfo,
  getLocalMinuteOfDay,
  getLightDelta,
  getLightSeconds,
  getLightSummary,
  getPosition,
  getRegime,
  getEquinoxes,
  getYearLightCurve,
  getYearMilestones,
  MAX_TRANSITION_DAYS,
};
